import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
	Container,
	ContainerPadding,
	CTA,
	Text,
	TokenTextAppearance,
	FilterTag,
	ContainerGutter,
} from '@volkswagen-onehub/components-core';
import { useDispatch } from 'react-redux';
import { InputError } from 'src/components';
import { useOneFormContext } from 'src/feature-app/OneForm';
import { Upload } from '@volkswagen-onehub/icons-core';
import { store } from 'src/feature-app';
import { OneFormState } from 'src/types';

interface FileProps {
	name?: string;
	label?: string;
	CTALabel?: string;
	required?: boolean;
	allowedTypes?: any[];
	maxMbSize?: number;
	maxFiles?: number;
	noPadding?: boolean;
}

export function InputFile(props: FileProps) {
	const { register, errors } = useFormContext();
	const fileInputRef = useRef(null);
	const {
		name,
		label,
		required,
		CTALabel,
		maxMbSize,
		allowedTypes,
		maxFiles,
		noPadding,
	} = props;
	const [inputFiles, setInputFiles] = useState([]);
	const [fileError, setFileError] = useState(false);
	const [maxFilesError, setMaxFilesError] = useState(false);
	const [maxSizeError, setMaxSizeError] = useState(false);
	const { error, setError } = useOneFormContext();
	const dispatch = useDispatch();
	const { formData }: OneFormState = store.getState();
	const { fields } = formData;

	const handleChange = (e: any) => {
		const file = e.target.files?.[0];

		if (!file || !isValidFile(file)) {
			setFileError(true);
			setError(true);
		} else {
			const maxFilesAllowed = maxFiles || 1;
			const updatedFiles = fields[name]
				? [...fields[name], file]
				: [file];
			if (updatedFiles.length > maxFilesAllowed) {
				setMaxFilesError(true);
			} else if (
				maxMbSize &&
				updatedFiles.reduce((acc, file) => acc + file.size, 0) >
					maxMbSize * 1024 * 1024
			) {
				setMaxSizeError(true);
			} else {
				dispatch({
					type: 'UPDATE_FIELDS',
					payload: { [name]: updatedFiles },
				});
				setInputFiles([...inputFiles, file.name]);
				setFileError(false);
				setMaxFilesError(false);
				setError(false);
			}
		}
	};

	const isValidFile = (file: File) => {
		const validType = allowedTypes
			? allowedTypes.includes(file.type)
			: true;
		const validSize = maxMbSize
			? maxMbSize * 1024 * 1024 > file.size
			: true;

		return validType && validSize;
	};

	const getLiteralTypes = () => {
		return allowedTypes
			? allowedTypes
					.map(mime => mime.split('/').pop().toUpperCase())
					.join(', ')
			: '';
	};

	const getUsedSize = (): number => {
		const files = fields[name];
		if (files) {
			const totalSize = files.reduce(
				(acc: number, file: File) => acc + file.size,
				0,
			);
			return parseFloat((totalSize / (1024 * 1024)).toFixed(2));
		}
		return 0;
	};

	const removeFile = (fileName: string) => {
		const updatedFiles = fields[name].filter(
			(file: File) => file.name !== fileName,
		);
		dispatch({ type: 'UPDATE_FIELDS', payload: { [name]: updatedFiles } });
		setInputFiles(updatedFiles.map((file: File) => file.name));
	};

	return (
		<div style={{ padding: noPadding ? '0' : '32px 0' }}>
			<div style={{ display: 'none' }}>
				<input
					type="file"
					name={name}
					accept="*/*"
					ref={fileInputRef}
					onChange={handleChange}
				/>
			</div>

			{label ? (
				<Container
					wrap="always"
					padding={{ bottom: ContainerPadding.dynamic0050 }}
				>
					<Text appearance={TokenTextAppearance.copy200}>
						{label} {required ? '' : '(opcional)'}
					</Text>
				</Container>
			) : null}

			<Container wrap="never" verticalAlign="center">
				<CTA
					onClick={e => {
						e.preventDefault();
						fileInputRef.current?.click();
					}}
					tag="button"
					emphasis="primary"
					size="small"
					icon={<Upload variant="small" />}
				>
					{CTALabel ? CTALabel : 'Subir Archivo'}
				</CTA>
				<Container
					wrap="always"
					padding={{ left: ContainerPadding.static200 }}
				>
					{maxMbSize ? (
						<Text appearance={TokenTextAppearance.copy200}>
							Tamaño máximo: {maxMbSize}MB{' '}
							{getUsedSize() > 0
								? `(usado ${getUsedSize()}MB )`
								: ''}
						</Text>
					) : null}
					{getLiteralTypes() ? (
						<Text appearance={TokenTextAppearance.copy200}>
							Archivo{allowedTypes?.length > 1 ? 's' : ''}{' '}
							soportado{allowedTypes?.length > 1 ? 's' : ''}:{' '}
							{getLiteralTypes()}
						</Text>
					) : null}
				</Container>
			</Container>
			{fileError && (
				<InputError className="input-error">
					Este archivo no es válido.
				</InputError>
			)}

			<Container
				wrap="never"
				padding={{ top: ContainerPadding.dynamic0050 }}
				gutter={ContainerGutter.dynamic0020}
			>
				{inputFiles &&
					inputFiles.map((file, index) => (
						<FilterTag
							key={index}
							iconPosition="end"
							text={file}
							active
							data-testid="filterTag"
							onClick={e => {
								e.preventDefault();
								if (fileInputRef.current)
									fileInputRef.current.value = '';
								removeFile(file);
							}}
						/>
					))}
			</Container>
			{maxFilesError && (
				<InputError className="input-error">
					Solo se permiten {maxFiles} archivo{maxFiles ? 's' : ''}.
				</InputError>
			)}
			{maxSizeError && (
				<InputError className="input-error">
					Solo se permite enviar {maxMbSize}MB.
				</InputError>
			)}
		</div>
	);
}
